import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { Navbar } from "@/components/landing/Navbar";
import { FooterSection } from "@/components/landing/FooterSection";
import {
 getProviderTotalCount,
 PROVIDERS_PER_DIRECTORY_PAGE,
} from "@/lib/seo.functions";

const SITE = "https://heallexa.com";

export const Route = createFileRoute("/doctors/directory/")({
 head: () => {
 const url = `${SITE}/doctors/directory`;
 const title = "Healthcare Provider Directory | Heallexa";
 const description =
 "Browse every healthcare provider listed on Heallexa, organized into numbered pages of 1,000 profiles each. Find doctors, dentists, therapists, and specialists across the United States.";
 return {
 meta: [
 { title },
 { name: "description", content: description },
 { property: "og:title", content: title },
 { property: "og:description", content: description },
 { property: "og:url", content: url },
 { property: "og:type", content: "website" },
 ],
 links: [{ rel: "canonical", href: url }],
 scripts: [
 {
 type: "application/ld+json",
 children: JSON.stringify({
 "@context": "https://schema.org",
 "@type": "CollectionPage",
 name: title,
 description,
 url,
 isPartOf: { "@id": `${SITE}/#website` },
 about: { "@type": "Thing", name: "Healthcare Providers" },
 breadcrumb: {
 "@type": "BreadcrumbList",
 itemListElement: [
 { "@type": "ListItem", position: 1, name: "Home", item: SITE },
 {
 "@type": "ListItem",
 position: 2,
 name: "Healthcare Provider Directory",
 item: url,
 },
 ],
 },
 }),
 },
 ],
 };
 },
 component: DoctorsDirectoryIndex,
});

function DoctorsDirectoryIndex() {
 const fetchCount = useServerFn(getProviderTotalCount);
 const { data, isLoading } = useQuery({
 queryKey: ["provider-total-count"],
 queryFn: () => fetchCount(),
 staleTime: 60 * 60 * 1000,
 });

 const total = data?.total ?? 0;
 const totalPages = total > 0 ? Math.ceil(total / PROVIDERS_PER_DIRECTORY_PAGE) : 0;

 // Group into blocks of 100 pages each (e.g. 0-99, 100-199, \u2026) so the index
 // page stays readable even with thousands of pages.
 const blockSize = 100;
 const blocks: Array<{ start: number; end: number }> = [];
 for (let s = 0; s < totalPages; s += blockSize) {
 blocks.push({ start: s, end: Math.min(s + blockSize - 1, totalPages - 1) });
 }

 return (
 <div className="min-h-screen bg-background">
 <Navbar />
 <main className="mx-auto max-w-[1200px] px-6 pt-32 md:pt-24 pb-16">
 <nav className="text-sm text-muted-foreground mb-6">
 <a href="/sitemap" className="hover:text-primary transition-colors">Sitemap</a>
 <span className="mx-2">/</span>
 <span className="text-foreground">Healthcare Provider Directory</span>
 </nav>

 <header className="mb-10">
 <p className="text-[13px] uppercase tracking-[0.15em] text-muted-foreground mb-4">Heallexa Directory</p>
 <h1 className="font-display text-[clamp(1.8rem,3vw,2.5rem)] font-medium tracking-[-0.03em] leading-[1.15] text-foreground">
 Healthcare Provider Directory
 </h1>
 <p className="mt-3 text-[14px] text-muted-foreground max-w-[560px]">
 Browse {total.toLocaleString()} healthcare provider profiles on Heallexa, organized
 into {totalPages.toLocaleString()} numbered pages of{" "}
 {PROVIDERS_PER_DIRECTORY_PAGE.toLocaleString()} providers each.
 </p>
 </header>

 {isLoading ? (
 <div className="card-surface p-10 text-center text-[14px] text-muted-foreground rounded-md">Loading directory\u2026</div>
 ) : (
 <div className="space-y-10">
 {blocks.map((block) => (
 <section key={block.start}>
 <h2 className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground mb-4">
 Pages {block.start.toLocaleString()}\u2013{block.end.toLocaleString()}
 </h2>
 <div className="flex flex-wrap gap-2">
 {Array.from({ length: block.end - block.start + 1 }).map((_, i) => {
 const p = block.start + i;
 return (
 <a
 key={p}
 href={`/doctors/directory/${p}`}
 className="inline-flex items-center h-8 px-2.5 text-[12px] tabular-nums rounded-md border border-border bg-background text-foreground hover:border-primary/40 hover:bg-primary/5 transition-colors"
 >
 {p}
 </a>
 );
 })}
 </div>
 </section>
 ))}
 </div>
 )}
 </main>
 <FooterSection />
 </div>
 );
}
